import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type Status = "checking" | "valid" | "success" | "already" | "invalid" | "error";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const [status, setStatus] = useState<Status>("checking");
  const { token } = Route.useSearch();

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      if (!token) {
        setStatus("invalid");
        return;
      }
      try {
        const response = await fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`);
        const result = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (result.valid) setStatus("valid");
        else if (result.reason === "already_unsubscribed") setStatus("already");
        else setStatus("invalid");
      } catch {
        if (!cancelled) setStatus("error");
      }
    }
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirmUnsubscribe = async () => {
    setStatus("checking");
    try {
      const response = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const result = await response.json().catch(() => ({}));
      if (result.success) setStatus("success");
      else if (result.reason === "already_unsubscribed") setStatus("already");
      else setStatus(response.ok ? "invalid" : "error");
    } catch {
      setStatus("error");
    }
  };

  const copy = {
    checking: {
      title: "Checking your unsubscribe link",
      body: "Please wait while we confirm this request.",
    },
    valid: {
      title: "Confirm unsubscribe",
      body: "You can stop receiving app emails from Homestead Construction at this address.",
    },
    success: {
      title: "You’re unsubscribed",
      body: "This email address has been removed from future app emails.",
    },
    already: {
      title: "Already unsubscribed",
      body: "This email address has already been removed from future app emails.",
    },
    invalid: {
      title: "Link unavailable",
      body: "This unsubscribe link is invalid or expired.",
    },
    error: {
      title: "Something went wrong",
      body: "We could not process this request right now. Please try again in a moment.",
    },
  }[status];

  return (
    <main className="min-h-screen bg-background px-4 py-24">
      <section className="mx-auto max-w-xl border border-border bg-card p-8 text-center shadow-sm">
        <p className="eyebrow mb-3">Homestead Construction</p>
        <h1 className="text-3xl font-bold text-foreground">{copy.title}</h1>
        <p className="mt-4 text-muted-foreground">{copy.body}</p>
        {status === "valid" ? (
          <button
            onClick={confirmUnsubscribe}
            className="mt-7 inline-flex items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Confirm unsubscribe
          </button>
        ) : null}
        {status !== "checking" && status !== "valid" ? (
          <a
            href="/"
            className="mt-7 inline-flex items-center justify-center rounded-md border border-input px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
          >
            Back to website
          </a>
        ) : null}
      </section>
    </main>
  );
}